import React from 'react'

import {Box} from '@vio/atlas/helpers/Box'
import {useIsReferralProgramEnabled} from '@vio/atlas/hooks/useIsReferralProgramEnabled'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {defaultBrand} from '@vio/core/src/brand/config'
import {filterAllowedCodes} from '@vio/core/src/brand/utils'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'

import {Alerts} from './Alerts'
import {MobileCurrencySelector} from './CurrencySelector/Mobile'
import {EmailPreferences} from './EmailPreferences'
import {GetHelpLink} from './GetHelpLink'
import {HowItWorks} from './HowItWorks'
import {InviteFriendsToGems} from './InviteFriendsToGems'
import {MobileLanguageSelector} from './LanguageSelector/Mobile'
import {MyBookingsLink} from './MyBookingsLink'
import {ShareYourFeedbackLink} from './ShareYourFeedbackLink'
import {PropsType} from './types'

export const PreferenceSelector = ({
  availableLanguages,
  brand = defaultBrand,
  currenciesList,
  currentCurrency,
  currentLanguage,
  hasCurrencySelector = true,
  hasLanguageSelector = true,
  myBookingsUrl,
  onChangeCurrency,
  onClickCurrency,
  onChangeLanguage,
  onClickLanguage,
  onClickReferral,
  onClickAlerts,
  onClickEmailPreferences,
  onShareFeedbackClick,
  popularCurrenciesList,
  showShareYourFeedback,
  hideShareYourFeedback,
  hideReferral,
  hideMyBooking,
  onChangePriceDisplay,
  showTotalPrices = false,
  showPriceDisplaySetting = false,
  getTheAppSlot = null,
  includeTaxes = false,
  howItWorksLink = ''
}: PropsType) => {
  const toggle = useToggle()
  const {
    auth: {hasAccountPreferences, hasEmailPreferences, hasGetHelpLink},
    retention: {hasPriceWatch}
  } = useBrandCapabilities()
  const {
    brand: {languageCodes, currencyCodes}
  } = useBrand()
  const showReferral = useIsReferralProgramEnabled(brand)
  const shouldShowEmailPreferences =
    toggle('4f732294-notification-preferences', false, true) &&
    onClickEmailPreferences

  return (
    <Box padding="s500" paddingTop="s300">
      {hasLanguageSelector && (
        <MobileLanguageSelector
          availableLanguages={filterAllowedCodes(
            availableLanguages,
            languageCodes
          )}
          currentLanguage={currentLanguage}
          onChangeLanguage={onChangeLanguage}
          onClickLanguage={onClickLanguage}
        />
      )}
      {hasCurrencySelector && (
        <MobileCurrencySelector
          currenciesList={filterAllowedCodes(currenciesList, currencyCodes)}
          popularCurrenciesList={filterAllowedCodes(
            popularCurrenciesList,
            currencyCodes
          )}
          currentCurrency={currentCurrency}
          onChangeCurrency={onChangeCurrency}
          onClickCurrency={onClickCurrency}
          showTotalPrices={showTotalPrices}
          showPriceDisplaySetting={showPriceDisplaySetting}
          onChangePriceDisplay={onChangePriceDisplay}
          includeTaxes={includeTaxes}
        />
      )}
      {hasPriceWatch && <Alerts onClick={() => onClickAlerts?.()} />}

      {hasAccountPreferences &&
        hasEmailPreferences &&
        shouldShowEmailPreferences && (
          <EmailPreferences onClick={() => onClickEmailPreferences?.()} />
        )}
      {myBookingsUrl && !hideMyBooking && (
        <MyBookingsLink myBookingsUrl={myBookingsUrl} />
      )}
      {showReferral && !hideReferral && onClickReferral && (
        <InviteFriendsToGems onClick={onClickReferral} />
      )}

      {showShareYourFeedback && !hideShareYourFeedback && (
        <ShareYourFeedbackLink onClick={onShareFeedbackClick} />
      )}

      <HowItWorks brand={brand} link={howItWorksLink} />

      {hasAccountPreferences && getTheAppSlot}
      {hasGetHelpLink && <GetHelpLink brand={brand} />}
    </Box>
  )
}
